import { NavLink, Link, useNavigate } from 'react-router-dom';
import { Home, MessageCircleHeart, BookOpen, TrendingUp, User, Sparkles, HandHeart, Users, Milestone, Phone, BookMarked, LogOut, Wind, GraduationCap, MessagesSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import NotificationBell from '@/components/NotificationBell';
import { useAuth } from '@/context/AuthContext';

export default function TopNav() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const role = user?.is_admin ? 'admin' : (user?.role || 'member');
  const isSupporter = role === 'supporter';

  const doSignOut = () => {
    if (!window.confirm('Sign out of Brotherhood+?')) return;
    logout();
    nav('/');
  };

  const memberItems = [
    { to: '/app', icon: Home, label: t('nav.home'), testid: 'topnav-home', end: true },
    { to: '/app/learn', icon: BookOpen, label: t('nav.learn'), testid: 'topnav-learn' },
    { to: '/app/coach', icon: MessageCircleHeart, label: t('nav.coach'), testid: 'topnav-coach' },
    { to: '/app/community', icon: MessagesSquare, label: 'Community', testid: 'topnav-community' },
    { to: '/app/plans', icon: Milestone, label: t('nav.plans', 'Plans'), testid: 'topnav-plans' },
    { to: '/app/journal', icon: Sparkles, label: t('nav.journal'), testid: 'topnav-journal' },
    { to: '/app/lounge', icon: Wind, label: 'Lounge', testid: 'topnav-lounge' },
    { to: '/app/support', icon: HandHeart, label: t('nav.support'), testid: 'topnav-support' },
    { to: '/app/therapists', icon: Users, label: t('nav.therapy'), testid: 'topnav-therapists' },
    { to: '/app/resources', icon: BookMarked, label: 'Resources', testid: 'topnav-resources' },
    { to: '/app/progress', icon: TrendingUp, label: t('nav.progress'), testid: 'topnav-progress' },
    { to: '/app/profile', icon: User, label: t('nav.profile'), testid: 'topnav-profile' },
  ];
  const supporterItems = [
    { to: '/app/support-home', icon: HandHeart, label: 'Support Home', testid: 'topnav-support-home', end: true },
    { to: '/app/support-coach', icon: MessageCircleHeart, label: 'Support Coach', testid: 'topnav-support-coach' },
    { to: '/app/support-community', icon: MessagesSquare, label: 'Community', testid: 'topnav-supporter-community' },
    { to: '/app/support-university', icon: GraduationCap, label: 'University', testid: 'topnav-support-university' },
    { to: '/app/therapists', icon: Users, label: 'Therapists', testid: 'topnav-supporter-therapists' },
    { to: '/app/resources', icon: BookMarked, label: 'Resources', testid: 'topnav-supporter-resources' },
    { to: '/app/support-crisis', icon: Phone, label: 'Crisis Help', testid: 'topnav-support-crisis' },
    { to: '/app/profile', icon: User, label: t('nav.profile'), testid: 'topnav-profile' },
  ];
  const items = isSupporter ? supporterItems : memberItems;
  const brandHome = isSupporter ? '/app/support-home' : '/app';
  return (
    <header
      data-testid="top-nav"
      className="hidden lg:block sticky top-0 z-40 bg-[#F9F8F6]/85 backdrop-blur-xl border-b border-navy/5"
    >
      <div className="max-w-[1720px] mx-auto px-6 2xl:px-8 h-16 flex items-center justify-between gap-2 xl:gap-3">
        <Link to={brandHome} data-testid="topnav-brand" className="flex items-center gap-2 group shrink-0">
          <span className="w-8 h-8 rounded-2xl bg-navy text-white flex items-center justify-center font-heading text-sm">B<span className="text-gold">+</span></span>
          <span className="hidden 2xl:inline font-heading text-lg tracking-tight bh-goldshine">Brotherhood<span className="text-gold">+</span></span>
        </Link>
        <nav className="flex items-center gap-0.5 min-w-0 flex-1 justify-center overflow-x-auto [&::-webkit-scrollbar]:hidden">
          {items.map(({ to, icon: Icon, label, testid, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              title={label}
              aria-label={label}
              className={({ isActive }) =>
                `inline-flex items-center gap-1.5 px-2 xl:px-2.5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-smooth ${
                  isActive ? 'bg-navy text-white' : 'text-navy/70 hover:text-navy hover:bg-beige'
                }`
              }
            >
              <Icon size={15} strokeWidth={1.75} />
              <span className="hidden xl:inline">{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3 xl:gap-4 shrink-0">
          {/* Search moved to floating FAB (SearchFAB.js) — freed top-nav space */}

          <div className="relative group">
            <NotificationBell />
            <span
              role="tooltip"
              className="pointer-events-none absolute top-full mt-1.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-navy text-white text-[11px] font-medium px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150 shadow-lg z-50"
            >
              Notifications
            </span>
          </div>

          <div className="relative group hidden md:block">
            <button
              data-testid="topnav-signout"
              onClick={doSignOut}
              aria-label="Sign out of Brotherhood+"
              className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-navy/10 bg-white text-navy/70 hover:text-red-700 hover:border-red-200 hover:bg-red-50 transition-smooth focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
            >
              <LogOut size={14} strokeWidth={1.75}/>
            </button>
            <span
              role="tooltip"
              className="pointer-events-none absolute top-full mt-1.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-navy text-white text-[11px] font-medium px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150 shadow-lg z-50"
            >
              Sign out
            </span>
          </div>
        </div>
      </div>
    </header>
  );
}
